import React, { useState, useRef, useEffect } from 'react';
import {
    Send,
    Calculator,
    DollarSign,
    CheckCircle,
    Ruler,
    TrendingUp,
    BarChart3,
    Sparkles,
    Loader2,
    X,
    Building2,
    Lightbulb,
    ChevronRight,
    Plus,
    Settings,
    FileText,
    Maximize2,
    GitCompare,
    Layers
} from 'lucide-react';
import ModuleResponseCard from './ModuleResponseCard';
import SBAVisualizer from './SBAVisualizer';
import DevelopmentScenariosExplorer from './DevelopmentScenariosExplorer';
import ExpandableModal from './ExpandableModal';
import CustomAnalysisPanel from './CustomAnalysisPanel';
import ScenarioComparison from './ScenarioComparison';
import { fsiV2Service, FSIV2Module, FSIV2AnalysisResponse } from '../../services/fsiV2';

interface Message {
    id: string;
    type: 'user' | 'system' | 'module' | 'sba' | 'scenarios' | 'fullreport';
    content?: string;
    module?: FSIV2Module;
    data?: any;
    fullData?: FSIV2AnalysisResponse; // For full report
    timestamp: string;
    loading?: boolean;
    error?: string;
    recommendations?: NextRecommendation[];
}

interface NextRecommendation {
    id: string;
    label: string;
    description: string;
    modules: FSIV2Module[];
    params?: Record<string, any>;
    icon: 'sba' | 'financial' | 'scenarios' | 'compliance' | 'height' | 'basic';
}

interface QuickAction {
    id: string;
    label: string;
    icon: React.ReactNode;
    modules: FSIV2Module[];
    description: string;
    params?: Record<string, any>;
    isFullReport?: boolean;
}

interface ChatPanelProps {
    gid: number | null;
    plotName?: string;
    isOpen: boolean;
    onClose: () => void;
    onVisualizeTowers?: (scenario: any) => void;
}

const QUICK_ACTIONS: QuickAction[] = [
    {
        id: 'fsi',
        label: "What's the FSI?",
        icon: <Calculator className="w-4 h-4" />,
        modules: ['basic'],
        description: 'Get FSI breakdown and buildable area'
    },
    {
        id: 'costs',
        label: 'Show charges',
        icon: <DollarSign className="w-4 h-4" />,
        modules: ['basic', 'financial'],
        description: 'View development charges and estimates'
    },
    {
        id: 'compliance',
        label: 'Is it compliant?',
        icon: <CheckCircle className="w-4 h-4" />,
        modules: ['basic', 'compliance'],
        description: 'Check regulatory compliance'
    },
    {
        id: 'height',
        label: 'Height limits?',
        icon: <Ruler className="w-4 h-4" />,
        modules: ['basic', 'height'],
        description: 'View height restrictions'
    },
    {
        id: 'margins',
        label: 'Setbacks',
        icon: <Layers className="w-4 h-4" />,
        modules: ['basic', 'margins'],
        description: 'View required plot margins'
    },
    {
        id: 'sba',
        label: 'Plan units',
        icon: <BarChart3 className="w-4 h-4" />,
        modules: ['basic', 'sba'],
        description: 'Calculate SBA and unit planning'
    },
    {
        id: 'scenarios',
        label: 'Investment analysis',
        icon: <TrendingUp className="w-4 h-4" />,
        modules: ['basic', 'development_scenarios'],
        description: 'View all development scenarios'
    }
];

// Contextual recommendations based on current module
const getNextRecommendations = (currentModule: FSIV2Module, data: any): NextRecommendation[] => {
    const recommendations: NextRecommendation[] = [];

    switch (currentModule) {
        case 'basic':
            recommendations.push(
                {
                    id: 'check-compliance',
                    label: 'Check compliance status',
                    description: 'Verify if plot meets minimum requirements',
                    modules: ['compliance'],
                    icon: 'compliance'
                },
                {
                    id: 'view-financial',
                    label: 'See development charges',
                    description: 'Calculate FSI charges and costs',
                    modules: ['financial'],
                    icon: 'financial'
                },
                {
                    id: 'plan-development',
                    label: 'Plan apartment units',
                    description: 'Get SBA calculations with 1000 sqft units',
                    modules: ['sba'],
                    params: { unit_size: 1000, unit_measurement: 'sqft' },
                    icon: 'sba'
                }
            );
            break;

        case 'financial':
            recommendations.push(
                {
                    id: 'investment-analysis',
                    label: 'See investment options',
                    description: 'Compare development types and ROI',
                    modules: ['development_scenarios'],
                    icon: 'scenarios'
                },
                {
                    id: 'check-compliance',
                    label: 'Verify compliance',
                    description: 'Ensure plot meets requirements',
                    modules: ['compliance'],
                    icon: 'compliance'
                }
            );
            break;

        case 'compliance':
            if (data?.compliant) {
                recommendations.push(
                    {
                        id: 'plan-sba',
                        label: 'Plan your units',
                        description: 'Calculate SBA for 2BHK apartments',
                        modules: ['sba'],
                        params: { unit_size: 1000, unit_measurement: 'sqft' },
                        icon: 'sba'
                    },
                    {
                        id: 'see-scenarios',
                        label: 'Explore development options',
                        description: 'Compare G+4, G+14, G+22 options',
                        modules: ['development_scenarios'],
                        icon: 'scenarios'
                    }
                );
            } else {
                recommendations.push(
                    {
                        id: 'check-height',
                        label: 'Check height restrictions',
                        description: 'See what height is permissible',
                        modules: ['height'],
                        icon: 'height'
                    }
                );
            }
            break;

        case 'height':
            recommendations.push(
                {
                    id: 'plan-sba',
                    label: 'Plan building layout',
                    description: 'Get SBA with tower configurations',
                    modules: ['sba'],
                    params: { unit_size: 1000, unit_measurement: 'sqft' },
                    icon: 'sba'
                },
                {
                    id: 'see-scenarios',
                    label: 'Compare building types',
                    description: 'G+4 vs G+14 vs High-rise',
                    modules: ['development_scenarios'],
                    icon: 'scenarios'
                }
            );
            break;

        case 'sba':
            recommendations.push(
                {
                    id: 'try-1bhk',
                    label: 'Try 1BHK units',
                    description: 'Calculate with 650 sqft units',
                    modules: ['sba'],
                    params: { unit_size: 650, unit_measurement: 'sqft' },
                    icon: 'sba'
                },
                {
                    id: 'try-3bhk',
                    label: 'Try 3BHK units',
                    description: 'Calculate with 1400 sqft units',
                    modules: ['sba'],
                    params: { unit_size: 1400, unit_measurement: 'sqft' },
                    icon: 'sba'
                },
                {
                    id: 'full-investment',
                    label: 'Full investment analysis',
                    description: 'See ROI and cost breakdown',
                    modules: ['development_scenarios'],
                    icon: 'scenarios'
                }
            );
            break;

        case 'development_scenarios':
            recommendations.push(
                {
                    id: 'detailed-sba',
                    label: 'Detailed unit planning',
                    description: 'Plan tower configurations',
                    modules: ['sba'],
                    params: { unit_size: 1000, unit_measurement: 'sqft' },
                    icon: 'sba'
                },
                {
                    id: 'check-financial',
                    label: 'See exact charges',
                    description: 'Government charges breakdown',
                    modules: ['financial'],
                    icon: 'financial'
                }
            );
            break;

        default:
            recommendations.push(
                {
                    id: 'basic',
                    label: 'Start with FSI analysis',
                    description: 'Get basic plot information',
                    modules: ['basic'],
                    icon: 'basic'
                }
            );
    }

    return recommendations;
};

const getRecommendationIcon = (icon: string) => {
    switch (icon) {
        case 'sba': return <BarChart3 className="w-4 h-4" />;
        case 'financial': return <DollarSign className="w-4 h-4" />;
        case 'scenarios': return <TrendingUp className="w-4 h-4" />;
        case 'compliance': return <CheckCircle className="w-4 h-4" />;
        case 'height': return <Ruler className="w-4 h-4" />;
        case 'basic': return <Building2 className="w-4 h-4" />;
        default: return <Lightbulb className="w-4 h-4" />;
    }
};

const ChatPanel: React.FC<ChatPanelProps> = ({ gid, plotName, isOpen, onClose, onVisualizeTowers }) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showUnitConfig, setShowUnitConfig] = useState(false);
    const [unitSize, setUnitSize] = useState(1000);
    const [unitMeasurement, setUnitMeasurement] = useState<'sqft' | 'sqm' | 'sqyd'>('sqft');
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Expanded modal state
    const [expandedModal, setExpandedModal] = useState<{
        isOpen: boolean;
        type: 'sba' | 'scenarios' | 'fullreport' | null;
        data: any;
        title: string;
    }>({ isOpen: false, type: null, data: null, title: '' });

    // Comparison state
    const [compareItems, setCompareItems] = useState<Array<{
        id: string;
        name: string;
        type: 'sba' | 'development';
        data: any;
    }>>([]);
    const [showComparison, setShowComparison] = useState(false);
    const [showCustomAnalysis, setShowCustomAnalysis] = useState(false);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    useEffect(() => {
        if (gid) {
            setMessages([{
                id: 'welcome',
                type: 'system',
                content: `Selected plot ${plotName || gid}. What would you like to know?`,
                timestamp: new Date().toISOString()
            }]);
        }
    }, [gid, plotName]);

    const addMessage = (message: Omit<Message, 'id' | 'timestamp'>) => {
        const newMessage: Message = {
            ...message,
            id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, newMessage]);
        return newMessage.id;
    };

    const updateMessage = (id: string, updates: Partial<Message>) => {
        setMessages(prev => prev.map(msg =>
            msg.id === id ? { ...msg, ...updates } : msg
        ));
    };

    // Handle full report separately
    const handleFullReport = async () => {
        if (!gid || isLoading) return;

        addMessage({ type: 'user', content: 'Generate full analysis report' });

        const loadingId = addMessage({
            type: 'fullreport',
            loading: true
        });

        setIsLoading(true);

        try {
            // Fetch all modules at once
            const modules: FSIV2Module[] = ['basic', 'financial', 'compliance', 'height', 'margins', 'sba', 'development_scenarios'];
            const response = await fsiV2Service.getAnalysis(gid, modules, {
                unit_size: unitSize,
                unit_measurement: unitMeasurement
            });

            updateMessage(loadingId, {
                loading: false,
                fullData: response as FSIV2AnalysisResponse
            });

        } catch (error: any) {
            updateMessage(loadingId, {
                loading: false,
                error: error.response?.data?.message || error.message || 'Failed to generate report'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleQuery = async (
        modules: FSIV2Module[],
        userLabel: string,
        params?: Record<string, any>,
        displayType?: 'module' | 'sba' | 'scenarios'
    ) => {
        if (!gid || isLoading) return;

        // Add user message
        addMessage({ type: 'user', content: userLabel });

        // Determine display type
        const msgType = displayType || (
            modules.includes('sba') ? 'sba' :
                modules.includes('development_scenarios') ? 'scenarios' : 'module'
        );

        // Add loading message
        const loadingId = addMessage({
            type: msgType,
            module: modules[modules.length - 1], // Last module is primary
            loading: true
        });

        setIsLoading(true);

        try {
            const response = await fsiV2Service.getAnalysis(gid, modules, params);

            // Get the primary module data
            const primaryModule = modules[modules.length - 1];
            const data = response[primaryModule];

            // Generate next recommendations
            const recommendations = getNextRecommendations(primaryModule, data);

            // Update with data
            updateMessage(loadingId, {
                loading: false,
                data: data,
                recommendations: recommendations
            });

        } catch (error: any) {
            updateMessage(loadingId, {
                loading: false,
                error: error.response?.data?.message || error.message || 'Failed to fetch data'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleQuickAction = async (action: QuickAction) => {
        await handleQuery(action.modules, action.label, action.params);
    };

    const handleRecommendation = async (rec: NextRecommendation) => {
        const params = rec.params || {};

        // If SBA, add current unit config
        if (rec.modules.includes('sba')) {
            params.unit_size = params.unit_size || unitSize;
            params.unit_measurement = params.unit_measurement || unitMeasurement;
        }

        await handleQuery(rec.modules, rec.label, params);
    };

    const handleSendMessage = async () => {
        if (!inputValue.trim() || !gid || isLoading) return;

        const userInput = inputValue.trim().toLowerCase();
        setInputValue('');

        // Check for full report keywords
        if (userInput.includes('full') || userInput.includes('complete') || userInput.includes('all') || userInput.includes('everything') || userInput.includes('report')) {
            await handleFullReport();
            return;
        }

        // Parse query
        let modulesToFetch: FSIV2Module[] = [];
        let params: Record<string, any> = {};

        // Check for unit size in query
        const unitMatch = userInput.match(/(\d+)\s*(sqft|sqm|sqyd|sq\s*ft|sq\s*m)/);
        if (unitMatch) {
            params.unit_size = parseInt(unitMatch[1]);
            params.unit_measurement = unitMatch[2].replace(/\s/g, '') as any;
        }

        // Check for tower count
        const towerMatch = userInput.match(/(\d+)\s*tower/);
        if (towerMatch) {
            params.num_towers = parseInt(towerMatch[1]);
        }

        // Parse modules
        if (userInput.includes('invest') || userInput.includes('roi') || userInput.includes('option') || userInput.includes('scenario')) {
            modulesToFetch = ['basic', 'development_scenarios'];
        } else if (userInput.includes('sba') || userInput.includes('saleable') || userInput.includes('unit') || userInput.includes('apartment') || userInput.includes('tower') || userInput.includes('bhk')) {
            modulesToFetch = ['basic', 'sba'];
            if (!params.unit_size) {
                params.unit_size = unitSize;
                params.unit_measurement = unitMeasurement;
            }
            // Check for specific BHK
            if (userInput.includes('1bhk') || userInput.includes('1 bhk')) {
                params.unit_size = 650;
                params.unit_measurement = 'sqft';
            } else if (userInput.includes('2bhk') || userInput.includes('2 bhk')) {
                params.unit_size = 1000;
                params.unit_measurement = 'sqft';
            } else if (userInput.includes('3bhk') || userInput.includes('3 bhk')) {
                params.unit_size = 1400;
                params.unit_measurement = 'sqft';
            }
        } else if (userInput.includes('cost') || userInput.includes('charge') || userInput.includes('financial') || userInput.includes('price') || userInput.includes('fee')) {
            modulesToFetch = ['basic', 'financial'];
        } else if (userInput.includes('compliant') || userInput.includes('compliance') || userInput.includes('legal') || userInput.includes('valid')) {
            modulesToFetch = ['basic', 'compliance'];
        } else if (userInput.includes('height') || userInput.includes('tall') || userInput.includes('floor') || userInput.includes('storey')) {
            modulesToFetch = ['basic', 'height'];
        } else if (userInput.includes('margin') || userInput.includes('setback')) {
            modulesToFetch = ['basic', 'margins'];
        } else if (userInput.includes('road')) {
            modulesToFetch = ['basic', 'roads'];
        } else if (userInput.includes('fsi') || userInput.includes('buildable') || userInput.includes('area') || userInput.includes('basic')) {
            modulesToFetch = ['basic'];
        } else {
            addMessage({
                type: 'system',
                content: "I can help you with: FSI analysis, development charges, compliance check, height limits, unit planning (SBA), investment scenarios, or a complete report. Try asking about any of these!"
            });
            return;
        }

        await handleQuery(modulesToFetch, inputValue.trim(), params);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    // Render full report content
    const renderFullReport = (data: FSIV2AnalysisResponse) => {
        return (
            <div className="space-y-4">
                {/* Report Header */}
                <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl p-4 text-white">
                    <div className="flex items-center gap-2 mb-1">
                        <FileText className="w-5 h-5" />
                        <h3 className="font-semibold">Complete Analysis Report</h3>
                    </div>
                    <p className="text-sm text-white/80">All modules analyzed for this plot</p>
                </div>

                {/* Basic Module */}
                {data.basic && (
                    <ModuleResponseCard module="basic" data={data.basic} />
                )}

                {/* Financial Module */}
                {data.financial && (
                    <ModuleResponseCard module="financial" data={data.financial} />
                )}

                {/* Compliance Module */}
                {data.compliance && (
                    <ModuleResponseCard module="compliance" data={data.compliance} />
                )}

                {/* Height Module */}
                {data.height && (
                    <ModuleResponseCard module="height" data={data.height} />
                )}

                {/* Margins Module */}
                {data.margins && (
                    <ModuleResponseCard module="margins" data={data.margins} />
                )}

                {/* SBA Module */}
                {data.sba && (
                    <div className="bg-white border border-cyan-200 rounded-xl overflow-hidden">
                        <div className="bg-gradient-to-r from-cyan-500 to-blue-500 px-4 py-3 flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-white" />
                            <h3 className="text-white font-medium">Unit Planning & Towers</h3>
                        </div>
                        <div className="p-4">
                            <SBAVisualizer
                                data={data.sba}
                                onViewOnMap={onVisualizeTowers}
                            />
                        </div>
                    </div>
                )}

                {/* Development Scenarios Module */}
                {data.development_scenarios && (
                    <div className="bg-white border border-indigo-200 rounded-xl overflow-hidden">
                        <div className="bg-gradient-to-r from-indigo-500 to-purple-500 px-4 py-3 flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-white" />
                            <h3 className="text-white font-medium">Investment Analysis</h3>
                        </div>
                        <div className="p-4">
                            <DevelopmentScenariosExplorer data={data.development_scenarios} />
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (!isOpen) return null;

    return (
        <div className="w-[420px] h-full bg-white/95 backdrop-blur-md shadow-2xl flex flex-col border-l border-gray-200">
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-4 flex items-center justify-between">
                <div>
                    <h2 className="text-white font-semibold flex items-center gap-2">
                        <Sparkles className="w-5 h-5" />
                        Plot Analysis
                    </h2>
                    {plotName && <p className="text-emerald-100 text-sm">{plotName}</p>}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowUnitConfig(!showUnitConfig)}
                        className={`text-white/80 hover:text-white p-2 rounded-lg transition-colors ${showUnitConfig ? 'bg-white/20' : 'hover:bg-white/10'}`}
                        title="Unit configuration"
                    >
                        <Settings className="w-4 h-4" />
                    </button>
                    <button
                        onClick={onClose}
                        className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Unit Configuration */}
            {showUnitConfig && (
                <div className="bg-teal-50 border-b border-teal-100 p-3">
                    <div className="flex items-center gap-3">
                        <div className="flex-1">
                            <label className="text-xs text-teal-700 mb-1 block">Default Unit Size</label>
                            <input
                                type="number"
                                value={unitSize}
                                onChange={(e) => setUnitSize(parseInt(e.target.value) || 1000)}
                                className="w-full px-2 py-1.5 text-sm rounded border border-teal-200 focus:ring-teal-500 focus:border-teal-500"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="text-xs text-teal-700 mb-1 block">Measurement</label>
                            <select
                                value={unitMeasurement}
                                onChange={(e) => setUnitMeasurement(e.target.value as any)}
                                className="w-full px-2 py-1.5 text-sm rounded border border-teal-200 focus:ring-teal-500 focus:border-teal-500"
                            >
                                <option value="sqft">Square Feet</option>
                                <option value="sqm">Square Meters</option>
                                <option value="sqyd">Square Yards</option>
                            </select>
                        </div>
                    </div>
                </div>
            )}

            {/* Quick Actions */}
            {gid && messages.length <= 1 && (
                <div className="p-4 border-b border-gray-100 bg-gray-50">
                    <p className="text-xs text-gray-500 mb-3 flex items-center gap-1">
                        <Lightbulb className="w-3 h-3" />
                        Quick queries:
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {QUICK_ACTIONS.map(action => (
                            <button
                                key={action.id}
                                onClick={() => handleQuickAction(action)}
                                disabled={isLoading}
                                className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-full text-sm text-gray-700 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title={action.description}
                            >
                                {action.icon}
                                <span>{action.label}</span>
                            </button>
                        ))}
                    </div>
                    {/* Full Report button */}
                    <button
                        onClick={handleFullReport}
                        disabled={isLoading}
                        className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-medium hover:from-emerald-600 hover:to-teal-600 transition-colors disabled:opacity-50"
                    >
                        <FileText className="w-5 h-5" />
                        Generate Complete Report
                    </button>

                    {/* Custom Analysis Toggle */}
                    <button
                        onClick={() => setShowCustomAnalysis(!showCustomAnalysis)}
                        className="w-full mt-2 flex items-center justify-between px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm transition-colors"
                    >
                        <span className="flex items-center gap-2">
                            <Settings className="w-4 h-4" />
                            Advanced Analysis
                        </span>
                        <span>{showCustomAnalysis ? '−' : '+'}</span>
                    </button>

                    {/* Collapsible Custom Analysis Panel */}
                    {showCustomAnalysis && gid && (
                        <div className="mt-3 bg-white rounded-xl border border-gray-200 p-3 max-h-[400px] overflow-y-auto">
                            <CustomAnalysisPanel
                                gid={gid}
                                isLoading={isLoading}
                                onAnalysisComplete={(data, params) => {
                                    addMessage({ type: 'user', content: `Custom analysis with ${Object.keys(params).filter(k => params[k as keyof typeof params]).length} parameters` });
                                    setShowCustomAnalysis(false);

                                    if (data.sba) {
                                        const recs = getNextRecommendations('sba', data.sba);
                                        addMessage({
                                            type: 'sba',
                                            data: data.sba,
                                            module: 'sba',
                                            recommendations: recs
                                        });
                                    }
                                    if (data.development_scenarios) {
                                        const recs = getNextRecommendations('development_scenarios', data.development_scenarios);
                                        addMessage({
                                            type: 'scenarios',
                                            data: data.development_scenarios,
                                            module: 'development_scenarios',
                                            recommendations: recs
                                        });
                                    }
                                    if (data.basic && !data.sba && !data.development_scenarios) {
                                        const recs = getNextRecommendations('basic', data.basic);
                                        addMessage({
                                            type: 'module',
                                            data: data.basic,
                                            module: 'basic',
                                            recommendations: recs
                                        });
                                    }
                                }}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {!gid ? (
                    <div className="flex items-center justify-center h-full text-gray-400 text-center">
                        <div>
                            <div className="text-4xl mb-3">📍</div>
                            <p>Click on a plot to start analysis</p>
                        </div>
                    </div>
                ) : (
                    messages.map(message => (
                        <div key={message.id} className="space-y-3">
                            {message.type === 'user' && (
                                <div className="flex justify-end">
                                    <div className="bg-emerald-500 text-white px-4 py-2 rounded-2xl rounded-tr-sm max-w-[85%]">
                                        {message.content}
                                    </div>
                                </div>
                            )}

                            {message.type === 'system' && (
                                <div className="flex justify-start">
                                    <div className="bg-gray-100 text-gray-700 px-4 py-2 rounded-2xl rounded-tl-sm max-w-[85%]">
                                        {message.content}
                                    </div>
                                </div>
                            )}

                            {/* Full Report */}
                            {message.type === 'fullreport' && (
                                <div className="w-full">
                                    {message.loading ? (
                                        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 flex flex-col items-center justify-center">
                                            <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mb-2" />
                                            <span className="text-gray-600 font-medium">Generating complete report...</span>
                                            <span className="text-gray-400 text-sm mt-1">Analyzing all modules</span>
                                        </div>
                                    ) : message.error ? (
                                        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                                            <p className="text-red-600 text-sm">{message.error}</p>
                                        </div>
                                    ) : message.fullData ? (
                                        renderFullReport(message.fullData)
                                    ) : null}
                                </div>
                            )}

                            {/* Regular module response */}
                            {message.type === 'module' && (
                                <div className="w-full">
                                    {message.loading ? (
                                        <div className="bg-gray-50 rounded-xl p-6 flex items-center justify-center">
                                            <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
                                            <span className="ml-2 text-gray-500">Analyzing {message.module}...</span>
                                        </div>
                                    ) : message.error ? (
                                        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                                            <p className="text-red-600 text-sm">{message.error}</p>
                                        </div>
                                    ) : message.data && message.module ? (
                                        <ModuleResponseCard
                                            module={message.module}
                                            data={message.data}
                                            timestamp={message.timestamp}
                                        />
                                    ) : null}
                                </div>
                            )}

                            {/* SBA Visualization */}
                            {message.type === 'sba' && (
                                <div className="w-full">
                                    {message.loading ? (
                                        <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-6 flex items-center justify-center">
                                            <Loader2 className="w-6 h-6 text-cyan-500 animate-spin" />
                                            <span className="ml-2 text-gray-500">Calculating unit configurations...</span>
                                        </div>
                                    ) : message.error ? (
                                        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                                            <p className="text-red-600 text-sm">{message.error}</p>
                                        </div>
                                    ) : message.data ? (
                                        <div className="bg-white border border-cyan-200 rounded-xl overflow-hidden">
                                            <div className="bg-gradient-to-r from-cyan-500 to-blue-500 px-4 py-3 flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <BarChart3 className="w-5 h-5 text-white" />
                                                    <h3 className="text-white font-medium">Unit Planning & Towers</h3>
                                                </div>
                                                <button
                                                    onClick={() => setExpandedModal({
                                                        isOpen: true,
                                                        type: 'sba',
                                                        data: message.data,
                                                        title: 'Unit Planning & Towers'
                                                    })}
                                                    className="text-white/80 hover:text-white hover:bg-white/10 p-1.5 rounded-lg transition-colors"
                                                    title="Expand fullscreen"
                                                >
                                                    <Maximize2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                            <div className="p-4">
                                                <SBAVisualizer
                                                    data={message.data}
                                                    onViewOnMap={onVisualizeTowers}
                                                />
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            )}

                            {/* Development Scenarios */}
                            {message.type === 'scenarios' && (
                                <div className="w-full">
                                    {message.loading ? (
                                        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 flex items-center justify-center">
                                            <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
                                            <span className="ml-2 text-gray-500">Analyzing investment scenarios...</span>
                                        </div>
                                    ) : message.error ? (
                                        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                                            <p className="text-red-600 text-sm">{message.error}</p>
                                        </div>
                                    ) : message.data ? (
                                        <div className="bg-white border border-indigo-200 rounded-xl overflow-hidden">
                                            <div className="bg-gradient-to-r from-indigo-500 to-purple-500 px-4 py-3 flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <TrendingUp className="w-5 h-5 text-white" />
                                                    <h3 className="text-white font-medium">Investment Analysis</h3>
                                                </div>
                                                <button
                                                    onClick={() => setExpandedModal({
                                                        isOpen: true,
                                                        type: 'scenarios',
                                                        data: message.data,
                                                        title: 'Investment Analysis'
                                                    })}
                                                    className="text-white/80 hover:text-white hover:bg-white/10 p-1.5 rounded-lg transition-colors"
                                                    title="Expand fullscreen"
                                                >
                                                    <Maximize2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                            <div className="p-4">
                                                <DevelopmentScenariosExplorer data={message.data} />
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            )}

                            {/* Next Recommendations */}
                            {message.recommendations && message.recommendations.length > 0 && !message.loading && (
                                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-3 border border-amber-200">
                                    <div className="flex items-center gap-1 text-amber-700 text-xs font-medium mb-2">
                                        <Lightbulb className="w-3 h-3" />
                                        <span>What's next?</span>
                                    </div>
                                    <div className="space-y-1">
                                        {message.recommendations.map(rec => (
                                            <button
                                                key={rec.id}
                                                onClick={() => handleRecommendation(rec)}
                                                disabled={isLoading}
                                                className="w-full text-left flex items-center gap-2 p-2 rounded-lg hover:bg-white/60 transition-colors group disabled:opacity-50"
                                            >
                                                <div className="p-1.5 bg-amber-100 rounded text-amber-600 group-hover:bg-amber-200">
                                                    {getRecommendationIcon(rec.icon)}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium text-gray-800">{rec.label}</div>
                                                    <div className="text-xs text-gray-500 truncate">{rec.description}</div>
                                                </div>
                                                <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-amber-600" />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            {gid && (
                <div className="p-4 border-t border-gray-100 bg-white">
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Ask about FSI, units, investment..."
                            disabled={isLoading}
                            className="flex-1 px-4 py-3 bg-gray-100 rounded-xl border-0 focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-colors disabled:opacity-50"
                        />
                        <button
                            onClick={handleSendMessage}
                            disabled={!inputValue.trim() || isLoading}
                            className="p-3 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                <Send className="w-5 h-5" />
                            )}
                        </button>
                    </div>
                    <div className="flex gap-2 mt-2">
                        <button
                            onClick={() => handleQuery(['basic', 'sba'], 'Plan 2BHK units', { unit_size: 1000, unit_measurement: 'sqft' })}
                            disabled={isLoading}
                            className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 px-2 py-1 rounded-full transition-colors disabled:opacity-50"
                        >
                            <Plus className="w-3 h-3 inline mr-1" />
                            2BHK Plan
                        </button>
                        <button
                            onClick={() => handleQuery(['basic', 'development_scenarios'], 'Investment options')}
                            disabled={isLoading}
                            className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 px-2 py-1 rounded-full transition-colors disabled:opacity-50"
                        >
                            <Plus className="w-3 h-3 inline mr-1" />
                            ROI Analysis
                        </button>
                        <button
                            onClick={handleFullReport}
                            disabled={isLoading}
                            className="text-xs bg-emerald-100 hover:bg-emerald-200 text-emerald-700 px-2 py-1 rounded-full transition-colors disabled:opacity-50"
                        >
                            <Sparkles className="w-3 h-3 inline mr-1" />
                            Full Report
                        </button>
                    </div>
                </div>
            )}

            {/* Comparison Panel */}
            {compareItems.length > 0 && (
                <div className="px-4 py-2 border-b border-gray-100">
                    <button
                        onClick={() => setShowComparison(!showComparison)}
                        className="w-full flex items-center justify-between py-2 px-3 bg-indigo-50 rounded-lg text-sm text-indigo-700 hover:bg-indigo-100 transition-colors"
                    >
                        <span className="flex items-center gap-2">
                            <GitCompare className="w-4 h-4" />
                            Compare Scenarios ({compareItems.length})
                        </span>
                        {showComparison ? '−' : '+'}
                    </button>
                    {showComparison && (
                        <div className="mt-2">
                            <ScenarioComparison
                                items={compareItems}
                                onRemove={(id) => setCompareItems(prev => prev.filter(item => item.id !== id))}
                                onClear={() => setCompareItems([])}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Expanded Modal */}
            <ExpandableModal
                isOpen={expandedModal.isOpen}
                onClose={() => setExpandedModal({ isOpen: false, type: null, data: null, title: '' })}
                title={expandedModal.title}
                subtitle={plotName}
                icon={expandedModal.type === 'sba' ? <BarChart3 className="w-5 h-5" /> : <TrendingUp className="w-5 h-5" />}
                headerColor={expandedModal.type === 'sba' ? 'from-cyan-500 to-blue-500' : 'from-indigo-500 to-purple-500'}
            >
                {expandedModal.type === 'sba' && expandedModal.data && (
                    <div className="max-w-4xl mx-auto">
                        <SBAVisualizer
                            data={expandedModal.data}
                            onViewOnMap={(scenario) => {
                                setExpandedModal({ isOpen: false, type: null, data: null, title: '' });
                                onVisualizeTowers?.(scenario);
                            }}
                        />
                    </div>
                )}
                {expandedModal.type === 'scenarios' && expandedModal.data && (
                    <div className="max-w-5xl mx-auto">
                        <DevelopmentScenariosExplorer data={expandedModal.data} />
                    </div>
                )}
            </ExpandableModal>
        </div>
    );
};

export default ChatPanel;


